import { type JSX, useEffect, useId, useRef, useState } from 'react';

/**
 * Who is signed in, and the two things they can do about it.
 *
 * One control, identical in the partner portal and the internal one. Before it,
 * each portal spelled its own identity out differently - a "Sign out" button
 * beside a "Public site" link in one, a line of muted text beside a link button
 * in the other - and neither showed WHO was signed in anywhere a reader looks
 * for it. The name sat at the foot of the sidebar, which rail mode hides.
 *
 * The portals do not share a user pool (two Clerk applications, one per
 * signed-in staff portal - a blast-radius decision), so this component takes an
 * identity rather than reading one: the shape is shared, the source is not.
 */

export interface AccountMenuLabels {
  /** Accessible name of the trigger, e.g. "Account". */
  readonly account: string;
  readonly signOut: string;
  readonly settings: string;
  /** Shown under the name in the open menu. */
  readonly signedInAs: string;
}

/**
 * The reader's language, offered where the rest of their own settings are.
 *
 * ONE language per person, and this is the only control for it anywhere in a
 * portal: it selects the interface AND the language the platform writes to them
 * in. Before this there were two - an interface language in the internal
 * portal's top bar, and, on the partner settings screen, an interface language
 * and a separate organization-wide "notification language" on the same card -
 * so the answer to "what language am I in" depended on which control you had
 * last touched.
 *
 * Radio menu items rather than a `<select>`, because a native dropdown opened
 * from inside a menu renders its options outside this element in some browsers:
 * the outside-click handler then sees the option as "elsewhere" and closes the
 * menu out from under the press. These are ordinary buttons in the same DOM.
 */
export interface AccountMenuLanguage {
  /** Group heading, e.g. "Language". */
  readonly label: string;
  /** The locale currently in force. */
  readonly value: string;
  readonly options: readonly { readonly value: string; readonly label: string }[];
  readonly onChange: (next: string) => void;
}

export interface AccountMenuProps {
  /** Display name. An email is a name here - it is what identifies the person. */
  readonly name: string;
  /**
   * Profile picture. When the identity provider federated a Google sign-in this
   * is the Google avatar, which is why it is a URL and not initials: the reader
   * recognises their own face faster than their own initials.
   *
   * Pass ONLY a picture the account actually has - initials are the fallback
   * for an account with none (founder, 2026-09-29). Clerk's `imageUrl` is never
   * empty: without a photo it is Clerk's generated silhouette, so callers gate
   * it on `user.hasImage`.
   */
  readonly imageUrl?: string | null;
  readonly labels: AccountMenuLabels;
  readonly onSignOut: () => void;
  /**
   * Opens the identity provider's own account screen. Omitted where there is no
   * provider to open - a development or pilot session has no personal account -
   * and the entry is then not rendered rather than rendered dead.
   */
  readonly onOpenSettings?: (() => void) | undefined;
  /**
   * The reader's language. Omitted on a surface that has no per-user language to
   * offer - the bakery tablet signs in a DEVICE with a PIN, not a person, so its
   * language belongs to the tablet and stays on its own settings screen.
   */
  readonly language?: AccountMenuLanguage | undefined;
  /** Extra entries, e.g. a portal-specific link. Rendered above sign out. */
  readonly children?: JSX.Element | null;
}

/**
 * Initials, for an identity with no picture.
 *
 * Taken from the first two words, or the first two characters of an email's
 * local part - "simon.tchedikian@example.com" reads as ST rather than as SI.
 */
export function initialsOf(name: string): string {
  const local = name.includes('@') ? (name.split('@')[0] ?? '') : name;
  const words = local.split(/[\s._-]+/).filter((word) => word !== '');
  if (words.length === 0) return '?';
  if (words.length === 1) return (words[0] ?? '').slice(0, 2).toUpperCase();
  return `${(words[0] ?? '').charAt(0)}${(words[1] ?? '').charAt(0)}`.toUpperCase();
}

export function AccountMenu({
  name,
  imageUrl,
  labels,
  onSignOut,
  onOpenSettings,
  language,
  children,
}: AccountMenuProps): JSX.Element {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [broken, setBroken] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  /*
   * Close on a click anywhere else and on Escape. `pointerdown` rather than
   * `click`: a menu that survives until mouseup stays open over whatever the
   * reader just pressed, and looks like it swallowed the press.
   */
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const showImage = typeof imageUrl === 'string' && imageUrl !== '' && !broken;

  return (
    <div className="cib-account" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className="cib-account__trigger"
        aria-label={labels.account}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen((previous) => !previous)}
      >
        {showImage ? (
          // `alt=""` on purpose: the button already carries the accessible name,
          // and "photo of X" after it is the same fact twice.
          <img
            className="cib-account__avatar"
            src={imageUrl}
            alt=""
            referrerPolicy="no-referrer"
            onError={() => setBroken(true)}
          />
        ) : (
          <span className="cib-account__avatar cib-account__avatar--initials" aria-hidden="true">
            {initialsOf(name)}
          </span>
        )}
      </button>

      {open ? (
        <div className="cib-account__menu" id={id} role="menu">
          <div className="cib-account__who">
            <p className="cib-account__name">{name}</p>
            <p className="cib-account__hint">{labels.signedInAs}</p>
          </div>
          {language !== undefined ? (
            /*
             * The rule below suggests <fieldset>, which is a FORM grouping. What
             * this needs is the ARIA `group` that WAI-ARIA requires around
             * `menuitemradio` children inside a `menu`: a fieldset here would be
             * invalid content for the role and would leave the two radios with no
             * accessible grouping at all.
             */
            // biome-ignore lint/a11y/useSemanticElements: see above - a menu group, not a form group.
            <div className="cib-account__group" role="group" aria-label={language.label}>
              <p className="cib-account__grouplabel">{language.label}</p>
              {language.options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={option.value === language.value}
                  className="cib-account__item cib-account__item--choice"
                  onClick={() => {
                    setOpen(false);
                    language.onChange(option.value);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : null}
          {children}
          {onOpenSettings !== undefined ? (
            <button
              type="button"
              role="menuitem"
              className="cib-account__item"
              onClick={() => {
                setOpen(false);
                onOpenSettings();
              }}
            >
              {labels.settings}
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="cib-account__item cib-account__item--danger"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
          >
            {labels.signOut}
          </button>
        </div>
      ) : null}
    </div>
  );
}
